import "./index.scss";

import { Box, Modal, Stack } from "@mui/material";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import Button from "components/Button";
import ConfirmationBox from "components/ConfirmationBox";
import ScreenLoader from "components/ScreenLoader";
import Tabs from "components/Tabs";
import { MSG_OPERATION_FAILED, MSG_OPERATION_SUCCESSFUL } from "constants/index";
import { logMessage, scrollToTop } from "helpers/utils";
import { ExerciseByCircuit, ExerciseByMuscleGroup } from "models/lookups";
import { WorkoutPlan } from "models/workoutplan";
import {
  getLkpExercisesByCircuit,
  getLkpExercisesByMuscleGroup,
  getLkpGoalsList,
} from "modules/lookup/lookupServices";
import { getProtocolList } from "modules/protocol/protocolServices";
import {
  addWorkoutPlanEntry,
  getLkpCardioWizardProtocolList,
  getLkpFlexibilityStretchesLowerBodyList,
  getLkpFlexibilityStretchesUpperBodyList,
  getLkpPerceivedExertionList,
  getWorkoutPlanById,
  updateWorkoutPlanEntry,
} from "modules/workout-plan/workoutPlanServices";
import { createContext, memo, useCallback, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useAppSelector } from "redux/hooks";

import PlanInfo from "./PlanInfo";
import WorkoutPlanCardio from "./WorkoutPlanCardio";
import WorkoutPlanFlexibility from "./WorkoutPlanFlexibility";
import WorkoutPlanStrength from "./WorkoutPlanStrength";

export const WorkoutPlanContext = createContext({ isWorkoutPlanTemplate: false });

const WorkoutPlanEditModal = (props: {
  memberName?: string;
  open: boolean;
  onClose: any;
  memberId?: number;
  workoutPlanId?: number;
  memberDob?: any;
}) => {
  const { memberName, open, onClose, memberDob, memberId, workoutPlanId } = props;
  const [loading, setLoading] = useState(false);
  const [perceivedExertionOptions, setPerceivedExertionOptions] = useState([]);
  const [planInfoGoalsOptions, setPlanInfoGoalsOptions] = useState([]);
  const [flexibilityUpperBodyStretchesOptions, setFlexibilityUpperBodyStretchesOptions] = useState(
    []
  );
  const [flexibilityLowerBodyStretchesOptions, setFlexibilityLowerBodyStretchesOptions] = useState(
    []
  );
  const [cardioWizardProtocols, setCardioWizardProtocols] = useState([]);
  const [strengthExerciseProtocols, setStrengthExerciseProtocols] = useState([]);
  const [exerciseByMuscleGroupOptions, setExerciseByMuscleGroupOptions] = useState<
    ExerciseByMuscleGroup[]
  >([]);
  const [exerciseByCircuitOptions, setExerciseByCircuitOptions] = useState<ExerciseByCircuit[]>([]);
  const [saveButtonReminder, setSaveButtonReminder] = useState("");
  const [strengthTabValue, setStrengthTabValue] = useState("defineProgram");
  const changeStrengthTabValue = (tabValue) => {
    setStrengthTabValue(tabValue);
  };
  const workoutPlanContext = useContext(WorkoutPlanContext);

  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [isWorkoutPlanTemplate, setIsWorkoutPlanTemplate] = useState(false);
  const { isPending } = useAppSelector((state) => state.app);
  const defaultEmptyValue: WorkoutPlan = {
    additional_goals: [],
    cardio_overall_goal: {
      daily_max_value: "0",
      daily_min_value: "0",
      goal: "TIME",
      weekly_max_value: "0",
      weekly_min_value: "0",
    },
    cardio_program_frequency: 1,
    cardio_workouts: [],
    goals: [],
    hasCardioProgramBeenSaved: false,
    hasFlexibilityProgramBeenSaved: false,
    hasStrengthProgramBeenSaved: false,
    heart_rate_max: 0,
    heart_rate_min: 0,
    heart_rate_rest: 0,
    instructor_notes: "",
    isCardioProgramIncluded: false,
    isFlexibilityProgramIncluded: false,
    isStrengthProgramIncluded: false,
    member_id: memberId || null,
    perceived_exertion: 1,
    strength_tab_value: "",
    strength_workouts: [],
    stretch_exercises: [],
    warm_up_notes: "",
    workout_plan_id: 0,
    workout_plan_name: "",
  };
  const [loadedWorkoutPlan, setLoadedWorkoutPlan] = useState(defaultEmptyValue);
  const methods = useForm<WorkoutPlan>({
    defaultValues: defaultEmptyValue,
  });
  const { trigger, watch, setValue, getValues } = methods;

  const watchIsFlexibilityProgramIncluded = watch("isFlexibilityProgramIncluded");
  const watchIsCardioProgramIncluded = watch("isCardioProgramIncluded");
  const watchIsStrengthProgramIncluded = watch("isStrengthProgramIncluded");

  const [tabs, setTabs] = useState([{ disabled: false, label: "Plan Info", value: "plan_info" }]);
  const [tabValue, setTabValue] = useState("plan_info");
  const [activeTabs, setActiveTabs] = useState([]);
  const [lastActiveTab, setLastActiveTab] = useState("plan_info");
  const [currentTab, setCurrentTab] = useState(0);
  const [backbutton, setBackButton] = useState(false);

  const [showDataWillBeLostModal, setShowDataWillBeLostModal] = useState(false);

  //Preparing data needed for the form
  useEffect(() => {
    if (open) {
      setLoading(true);
      setTabValue("plan_info");
      let promises: Promise<any>[] = [];

      setIsWorkoutPlanTemplate(!memberId);
      if (memberId || workoutPlanId) {
        //Get workout plan
        let param = {};
        if (memberId) {
          param = { member_id: memberId };
        } else {
          param = { workout_plan_id: workoutPlanId };
        }
        promises.push(loadWorkoutPlan(param));
      }

      if (!lookupsLoaded) {
        //Get plan_info goals' list
        promises.push(
          getLkpGoalsList().then((res) => {
            if (res.success) {
              let goals: any[] = [];
              Object.keys(res.data).forEach((e) => {
                goals.push({ label: res.data[e], value: e });
              });
              setPlanInfoGoalsOptions(goals);
            }
          })
        );

        //Get flexibility upper body stretches' list
        promises.push(
          getLkpFlexibilityStretchesUpperBodyList().then((res) => {
            if (res.success) {
              setFlexibilityUpperBodyStretchesOptions(res.data);
            }
          })
        );

        //Get flexibility lower body stretches' list
        promises.push(
          getLkpFlexibilityStretchesLowerBodyList().then((res) => {
            if (res.success) {
              setFlexibilityLowerBodyStretchesOptions(res.data);
            }
          })
        );

        //Get list for perceivedExertionOptions
        promises.push(
          getLkpPerceivedExertionList().then((res) => {
            if (res.success) {
              let entries: any[] = [];
              Object.keys(res.data).forEach((e) => {
                entries.push({ label: `${e} - ${res.data[e]}`, value: e });
              });
              setPerceivedExertionOptions(entries);
            }
          })
        );

        //Get cardio wizard protocols
        promises.push(
          getLkpCardioWizardProtocolList().then((res) => {
            if (res.success) {
              setCardioWizardProtocols(res.data);
            }
          })
        );

        //Get strength exercise protocols
        promises.push(
          getProtocolList({ sort_by: "PROTOCOL_TYPE", sort_order: "DESC" }).then((res) => {
            if (res.success) {
              setStrengthExerciseProtocols(res.data);
            }
          })
        );

        //Get exercises by muscle group
        promises.push(
          getLkpExercisesByMuscleGroup({ limit: 0 }).then((res) => {
            if (res.success) {
              setExerciseByMuscleGroupOptions(res.data);
            }
          })
        );

        //Get exercises by muscle group
        promises.push(
          getLkpExercisesByCircuit({ limit: 0 }).then((res) => {
            if (res.success) {
              setExerciseByCircuitOptions(res.data);
            }
          })
        );
      }

      Promise.all(promises).then((_v) => {
        setLoading(false);
        setLookupsLoaded(true);
      });
    }
  }, [lookupsLoaded, memberId, open, setValue, workoutPlanId]);

  // Get the last active tab
  const findLastIndex = (tabs: string | any[], show: boolean) => {
    for (let i = tabs.length - 1; i >= 0; i--) {
      if (tabs[i].disabled === show) return tabs[i].value;
    }
    return -1;
  };

  //Manipulate active tabs
  useEffect(() => {
    let tabsArray = [{ disabled: false, label: "Plan Info", value: "plan_info" }];

    tabsArray.push({
      disabled: !watchIsFlexibilityProgramIncluded,
      label: "Flexibility Program",
      value: "flexibility_program",
    });
    tabsArray.push({
      disabled: !watchIsCardioProgramIncluded,
      label: "Cardio Program",
      value: "cardio_program",
    });
    tabsArray.push({
      disabled: !watchIsStrengthProgramIncluded,
      label: "Strength Program",
      value: "strength_program",
    });
    setTabs(tabsArray);
    setLastActiveTab(findLastIndex(tabsArray, false));
  }, [
    watchIsFlexibilityProgramIncluded,
    watchIsCardioProgramIncluded,
    watchIsStrengthProgramIncluded,
  ]);

  useEffect(() => {
    const allActiveTabs = getActiveTabs(tabs, "disabled", false);
    const currentTabIndex = allActiveTabs.findIndex((tab) => tab.value === tabValue);

    setActiveTabs(allActiveTabs);
    setCurrentTab(currentTabIndex);

    if (currentTab !== 0) {
      setBackButton(true);
    } else {
      setBackButton(false);
    }
  }, [tabs, tabValue, currentTab]);

  useEffect(() => {
    if (watchIsStrengthProgramIncluded) {
      if (tabValue === "strength_program") {
        if (isWorkoutPlanTemplate) {
          setSaveButtonReminder("Remember to Set Targets before saving");
        } else {
          setSaveButtonReminder("Remember to Set Targets and Settings before saving");
        }
      } else {
        setSaveButtonReminder("");
      }
    }
  }, [tabValue, watchIsStrengthProgramIncluded, isWorkoutPlanTemplate]);

  useEffect(() => {
    console.log("Strength Tab Value is now ", strengthTabValue);
  }, [strengthTabValue]);

  //#region functions
  const loadWorkoutPlan = (args: unknown, isFromCopyTemplate = false) => {
    setLoading(true);
    return getWorkoutPlanById(args)
      .then((res) => {
        if (res.success) {
          const fetchedEntry = res.data;
          setValue("additional_goals", fetchedEntry.additional_goals || []);
          setValue("hasFlexibilityProgramBeenSaved", fetchedEntry.hasFlexibilityProgramBeenSaved);
          setValue("hasCardioProgramBeenSaved", fetchedEntry.hasCardioProgramBeenSaved);
          setValue("hasStrengthProgramBeenSaved", fetchedEntry.hasStrengthProgramBeenSaved);
          setValue("heart_rate_min", fetchedEntry.heart_rate_min || 0);
          setValue("heart_rate_max", fetchedEntry.heart_rate_max || 0);
          setValue("heart_rate_rest", fetchedEntry.heart_rate_rest || 0);
          setValue("cardio_overall_goal.goal", fetchedEntry.cardio_overall_goal.goal);
          setValue(
            "cardio_overall_goal.daily_max_value",
            fetchedEntry.cardio_overall_goal.daily_max_value || "30"
          );
          setValue(
            "cardio_overall_goal.daily_min_value",
            fetchedEntry.cardio_overall_goal.daily_min_value || "10"
          );
          setValue(
            "cardio_overall_goal.weekly_max_value",
            fetchedEntry.cardio_overall_goal.weekly_max_value || "30"
          );
          setValue(
            "cardio_overall_goal.weekly_min_value",
            fetchedEntry.cardio_overall_goal.weekly_min_value || "10"
          );
          setValue("cardio_workouts", fetchedEntry.cardio_workouts || []);
          setValue("goals", fetchedEntry.goals);
          setValue("cardio_program_frequency", fetchedEntry.cardio_program_frequency || 1);
          setValue("instructor_notes", fetchedEntry.instructor_notes);
          setValue("isCardioProgramIncluded", fetchedEntry.isCardioProgramIncluded);
          setValue("isFlexibilityProgramIncluded", fetchedEntry.isFlexibilityProgramIncluded);
          setValue("isStrengthProgramIncluded", fetchedEntry.isStrengthProgramIncluded);
          setValue("member_id", memberId ? memberId : fetchedEntry.member_id || null);
          setValue("perceived_exertion", fetchedEntry.perceived_exertion || 1);
          setValue("stretch_exercises", fetchedEntry.stretch_exercises || []);
          setValue("strength_workouts", fetchedEntry.strength_workouts || []);
          setValue("warm_up_notes", fetchedEntry.warm_up_notes);
          if (!isFromCopyTemplate) setValue("workout_plan_id", fetchedEntry.workout_plan_id);
          setValue("workout_plan_name", fetchedEntry.workout_plan_name);
        }
      })
      .finally(() => {
        if (lookupsLoaded) {
          setLoading(false);
        }
      });
  };

  // Get all active tabs
  const getActiveTabs = (array: { [x: string]: any }[], key: string | number, value: any) => {
    return array.flatMap((obj: { [x: string]: any }, index: any) =>
      obj[key] === value ? obj : []
    );
  };
  //#endregion functions

  //#region event handlers
  const handleBackClick = () => {
    setTabValue(activeTabs[currentTab - 1].value);
  };

  const navigateFromCurrentTab = (toTab?: string) => {
    if (tabValue === "plan_info") {
      trigger().then((validationResult) => {
        if (!validationResult) {
          scrollToTop(".personal-details-modal-container");
          return;
        }
        navigateToTab(toTab);
      });
      return;
    }
    if (tabValue === "cardio_program") {
      const cardioSessionPerWeek = +getValues("cardio_program_frequency");
      const cardioDailyMin = +getValues("cardio_overall_goal.daily_min_value");
      const cardioDailyMax = +getValues("cardio_overall_goal.daily_max_value");
      const cardioWorkouts = getValues("cardio_workouts");
      if (cardioSessionPerWeek < 0) {
        toast("The sessions per week is missing a value", { type: "error" });
        return;
      }
      if (cardioSessionPerWeek < 1) {
        toast("The Frequency has to be 1 or greater", { type: "error" });
        return;
      }
      if (!cardioDailyMin || !cardioDailyMax) {
        toast("The weekly or per session target range is missing a value", { type: "error" });
        return;
      }
      if (cardioWorkouts.length === 0) {
        toast("No Cardio Programs addded", { type: "error" });
        return;
      }
    }
    if (tabValue === "strength_program") {
      const strengthWorkouts = getValues("strength_workouts");
      if (strengthWorkouts.length === 0) {
        toast("No Strength Programs addded", { type: "error" });
        return;
      }
    }
    navigateToTab(toTab);
  };

  const navigateToTab = (toTab?: string) => {
    if (toTab) {
      setTabValue(toTab);
    } else {
      const indexOfCurrentTab = tabs.findIndex((f) => f.value === tabValue);
      const nextTab = tabs.find((f, i) => i > indexOfCurrentTab && !f.disabled);
      if (nextTab) {
        setTabValue(nextTab.value);
      } else {
        //The current tab is the last active tab; submit the form
        handleConfirm();
      }
    }
  };

  const handleCloseModal = useCallback(() => {
    setShowDataWillBeLostModal(true);
  }, []);

  const onCloseConfirm = (discardChanges = false) => {
    if (discardChanges) {
      setShowDataWillBeLostModal(false);
      onClose();
    } else {
      setShowDataWillBeLostModal(false);
    }
  };

  const handleConfirm = () => {
    let data = getValues();
    // console.log(`HERE IS THE SUBMITTED DATA\n`, data);
    logMessage({
      member_id: memberId,
      message: `WorkoutPlanEditModal: Submitted data (planID) is ${data.workout_plan_id}`,
    });
    setLoading(true);
    const createOrUpdateAPI = data.workout_plan_id ? updateWorkoutPlanEntry : addWorkoutPlanEntry;
    createOrUpdateAPI(data)
      .then((r) => {
        if (r.success) {
          toast(MSG_OPERATION_SUCCESSFUL, { type: "success" });
          onClose(true);
        }
      })
      .catch((e) => {
        console.log(e);
        toast(MSG_OPERATION_FAILED, { type: "error" });
      })
      .finally(() => setLoading(false));
  };
  //#endregion event handlers

  return (
    <WorkoutPlanContext.Provider value={{ isWorkoutPlanTemplate }}>
      <Modal
        closeAfterTransition
        BackdropProps={{
          timeout: 500,
        }}
        open={open}
        onClose={handleCloseModal}
      >
        <FormProvider {...methods}>
          <form>
            <Stack className="personal-details-modal-container workout-plan-edit-modal">
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                marginBottom="40px"
                className="header"
              >
                <h1>
                  Workout Plan Details{!isWorkoutPlanTemplate && !loading ? ` - ${memberName}` : ""}
                </h1>
                <DeleteIcon width="24px" height="24px" onClick={handleCloseModal} />
              </Stack>
              {loading ? (
                <ScreenLoader isScreenLoader />
              ) : (
                <>
                  <Tabs
                    value={tabValue}
                    // onChange={navigateFromCurrentTab}
                    tabs={tabs}
                    className="tabs"
                  />
                  <Box flexGrow={1} sx={{ paddingRight: "5px" }}>
                    {tabValue === "plan_info" && (
                      <PlanInfo
                        goalOptions={planInfoGoalsOptions}
                        loadWorkoutPlanFromCopy={(item) => {
                          item && loadWorkoutPlan({ workout_plan_id: item.id }, true);
                        }}
                        loadWorkoutPlanFromMemberCopy={(item) => {
                          item && loadWorkoutPlan({ member_id: item.id });
                        }}
                        isTemplate={!!!memberId}
                      />
                    )}
                    {tabValue === "flexibility_program" && (
                      <WorkoutPlanFlexibility
                        upperBodyStretchesOptions={flexibilityUpperBodyStretchesOptions}
                        lowerBodyStretchesOptions={flexibilityLowerBodyStretchesOptions}
                      />
                    )}
                    {tabValue === "cardio_program" && (
                      <WorkoutPlanCardio
                        perceivedExertionOptions={perceivedExertionOptions}
                        cardioWizardProtocols={cardioWizardProtocols}
                        memberDob={memberDob}
                      />
                    )}
                    {tabValue === "strength_program" && (
                      <WorkoutPlanStrength
                        strengthExerciseProtocols={strengthExerciseProtocols}
                        programs={getValues("strength_workouts")}
                        exerciseByMuscleGroupOptions={exerciseByMuscleGroupOptions}
                        exerciseByCircuitOptions={exerciseByCircuitOptions}
                        changeStrengthTabValue={changeStrengthTabValue}
                      />
                    )}
                  </Box>
                </>
              )}
              <Stack direction="column" justifyContent="flex-end">
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  marginTop="30px"
                >
                  <Stack direction="row" alignItems="center" spacing={3} flexGrow={1}>
                    <Box flexGrow={1}>
                      {backbutton === true && (
                        <Button
                          label="Back"
                          type="button"
                          className="text-button"
                          onClick={() => handleBackClick()}
                        />
                      )}
                    </Box>

                    <Button
                      type="button"
                      label="Cancel"
                      className="text-button"
                      onClick={handleCloseModal}
                    />
                    <Button
                      type="button"
                      label={tabValue === lastActiveTab ? "Save" : "Next"}
                      className="confirm-button"
                      loading={isPending}
                      onClick={() => navigateFromCurrentTab()}
                    />
                  </Stack>
                </Stack>
                <h3 className="in-line-reminder">{saveButtonReminder}</h3>
              </Stack>
            </Stack>
          </form>
        </FormProvider>
      </Modal>
      <ConfirmationBox
        open={showDataWillBeLostModal}
        onClose={onCloseConfirm}
        title={"Discard Changes?"}
        message={<>If answered Yes, all information filled will be lost.</>}
        type={"confirmation"}
      />
    </WorkoutPlanContext.Provider>
  );
};

export default memo(WorkoutPlanEditModal);
